import { Box, Button, Paper, Stack, Theme } from '@mui/material';
import { grey } from '@mui/material/colors';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { format } from 'date-fns';
import orderBy from 'lodash/orderBy';
import React from 'react';
import { homeStyles } from '.';
import { DisplayMarkup } from '../../components/display-markup';
import { ContentCategoryName, ContentDocument } from '../../types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    markup: {
      '& p': {
        margin: 0,
      },
      '& a': {
        color: 'aqua',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
      },
    },
    showLessMarkup: {
      WebkitLineClamp: 10,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      '& p': {
        margin: 0,
      },
      '& a': {
        color: 'aqua',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
      },
    },
  }),
);

// Read More Function
function ShowMore(props: { item: ContentDocument; isMobile: boolean }) {
  // Props
  const { item, isMobile } = props;
  // State
  const [readMore, setReadMore] = React.useState(true);
  // Hooks
  const classes = useStyles();

  const formatDate = format(
    item.DateCreated ? new Date(item.DateCreated) : new Date(),
    'MMMM dd, yyyy',
  );

  return (
    <Box>
      <Box
        sx={{
          color: '#fff',
          fontSize: '15px',
          fontFamily: 'Lato',
          minHeight: '130px',
          paddingRight: '10px',
          overflow: 'hidden',
          paddingTop: '-10px',
        }}
      >
        <Box
          sx={{
            fontSize: ' 1.70em',
            lineHeight: 'normal',
            marginTop: '5px',
            paddingRight: '20px',
          }}
        >
          {item.Title}
        </Box>
        <Box
          sx={{
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#e0e0e0',
            py: 2,
          }}
        >
          {formatDate}
        </Box>

        <DisplayMarkup
          markup={item.Description}
          className={!readMore ? classes.markup : classes.showLessMarkup}
        />
      </Box>

      {item.Description
        ? (isMobile
            ? item.Description.length > 400
            : item.Description.length > 500) && (
            <Button
              size="small"
              color="secondary"
              onClick={() => setReadMore(!readMore)}
              sx={{
                float: 'right',
                mt: 1,
                mr: 1,
                fontFamily: 'Lato',
                fontWeight: 'bold',
              }}
            >
              {readMore ? 'Read More' : 'Close'}
            </Button>
          )
        : null}
    </Box>
  );
}

export function NewsAndNotes(props: {
  content: ContentDocument[];
  loading: boolean;
  isMobile: boolean;
}) {
  // Props
  const { content, loading, isMobile } = props;
  // State
  const [newPage, setNewPage] = React.useState<number>(1);
  const [itemsPerPage] = React.useState<number>(1);
  const dataFiltered = content.filter(
    (c) => c.Category === ContentCategoryName['News & Notes'],
  );
  const data = orderBy(dataFiltered, 'DdocIndex');

  // Pagination
  const handlePage = (e: React.ChangeEvent<unknown>, page: number) => {
    setNewPage(page);
  };

  // Sort Items
  const indexOfLastItem = newPage * itemsPerPage;
  const indexofFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data.slice(indexofFirstItem, indexOfLastItem);

  // Create Sorted Item Element
  const displayNews = currentItems.map((i) => {
    return (
      <Box
        key={i.DdocId}
        sx={{
          minHeight: { xs: 470, md: 675 },
          px: { xs: 2, sm: 5 },
        }}
      >
        <img
          src={i.ThumbnailName || i.ThumbnailContent || undefined}
          alt={i.Title || undefined}
          style={{
            width: '100%',
            height: 'auto',
            aspectRatio: '3/2',
            borderRadius: 5,
            boxShadow: `0px 0px 5px 1px ${grey[900]}`,
            marginBottom: 2,
          }}
        />

        {i.Description ? <ShowMore item={i} isMobile={isMobile} /> : null}
      </Box>
    );
  });

  return (
    <Paper sx={{ ...homeStyles.section }}>
      <Stack
        width="100%"
        flex={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        px={{ xs: 0, sm: 2 }}
        pb={2}
      >
        <Box py={3} sx={{ ...homeStyles.title }}>
          NEWS & NOTES
        </Box>

        {!loading && data.length ? (
          <Box>{displayNews}</Box>
        ) : (
          <Skeleton
            variant="rectangular"
            style={{ marginBottom: '10px', boxShadow: '2px 2px 5px gray' }}
          />
        )}

        <Pagination
          count={Math.ceil(data.length / 1)}
          variant="text"
          onChange={handlePage}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            mt: 2,
            ml: 'auto',
            mr: 'auto',
            '& .MuiPaginationItem-root': {
              color: '#fff',
              fontFamily: 'Lato',
              fontWeight: 300,
              fontSize: { xs: 12, sm: 18 },
            },
            '& .MuiPaginationItem-page.Mui-selected': {
              color: '#12D88E',
            },
            '& .MuiPaginationItem-page:hover': {
              backgroundColor: '#12D88E80',
            },
          }}
        />
      </Stack>
    </Paper>
  );
}
